// React
import React, { useMemo, useState } from "react";

// Third party imports
import { useTranslation } from "react-i18next";

// Utils
import { STATUS_MAP, STATUS_SUBTASKS, TYPES_MAP } from "utils/consts.jsx";

import { daysFromToday, isInternalHref, NONE_KEY } from "./blockUtils";

// Rows shown in the panel before "Show all"
export const COMPACT_ROWS = 5;

// Work statuses (tasks, subtasks) and client statuses: i18n key and colour
export const WORK_STATUSES = ["TODO", "IN_PROGRESS", "IN_REVIEW", "TO_UPLOAD", "DELIVERED"];
export const COMPANY_STATUSES = ["ACTIVE", "STANDBY", "NOTACTIVE"];
const STATUS_KEYS = {
  TODO: "todo",
  IN_PROGRESS: "inProgress",
  IN_REVIEW: "inReview",
  TO_UPLOAD: "toUpload",
  DELIVERED: "delivered",
  ACTIVE: "active",
  NOTACTIVE: "notActive",
  STANDBY: "standBy",
};

export const statusColor = (status) =>
  STATUS_SUBTASKS[status]?.color || STATUS_MAP[status]?.text || "#9ca3af";

export const useStatusLabel = () => {
  const { t } = useTranslation("aiAssistant");
  return (status) =>
    STATUS_KEYS[status] ? t(`blocks.status.${STATUS_KEYS[status]}`) : String(status || "");
};

// ---------------------------------------------------------------------------------------------
// Formatting

const toNumber = (value) => {
  const number = typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));
  return Number.isFinite(number) ? number : null;
};

export const useFormatters = () => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "it";
  return useMemo(() => {
    const thisYear = new Date().getFullYear();
    const dayShort = new Intl.DateTimeFormat(language, { day: "numeric", month: "short" });
    const dayLong = new Intl.DateTimeFormat(language, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const money = new Intl.NumberFormat(language, { style: "currency", currency: "EUR" });
    const number = new Intl.NumberFormat(language);
    const day = (value) => {
      const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return "";
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      if (Number.isNaN(date.getTime())) return "";
      return (date.getFullYear() === thisYear ? dayShort : dayLong).format(date);
    };
    return {
      day,
      // "YYYY-MM-DD HH:MM" → "3 nov, 14:05"
      moment: (value) => {
        const text = day(value);
        const time = String(value ?? "").match(/\s(\d{2}:\d{2})/);
        return text && time ? `${text}, ${time[1]}` : text;
      },
      money: (value) => {
        const amount = toNumber(value);
        return amount === null ? "" : money.format(amount);
      },
      number: (value) => {
        const amount = toNumber(value);
        return amount === null ? "" : number.format(amount);
      },
    };
  }, [language]);
};

export const itemsOf = (data) => (Array.isArray(data?.items) ? data.items : []);

/** Header props of a list block: title, view subtitle, total, "mostrati N di count". */
export const useFrame = (data, items, title, view) => {
  const { t } = useTranslation("aiAssistant");
  return {
    title,
    subtitle: view && view !== "list" ? t(`blocks.views.${view}`) : undefined,
    count: typeof data?.count === "number" ? data.count : items.length,
    shown: items.length,
    truncated: Boolean(data?.truncated),
  };
};

// Non-empty values joined with " · "
export const joinMeta = (...values) =>
  values
    .flat()
    .filter((value) => value !== null && value !== undefined && value !== false && value !== "")
    .join(" · ");

// ---------------------------------------------------------------------------------------------
// Small pieces

/** In-app link of a block ("/x" only, like AiMarkdown); plain text otherwise. */
// bare: no hover style of its own (chips and buttons bring theirs)
export function BlockLink({ href, onNavigate, className = "", bare = false, children }) {
  if (!isInternalHref(href)) return <span className={className}>{children}</span>;
  const hover = bare ? "" : "hover:text-primary-strong hover:underline";
  return (
    <a
      href={href}
      className={`rounded-sm focus-visible:outline-2 focus-visible:outline-primary ${hover} ${className}`}
      onClick={(event) => {
        // ⌘/Ctrl/Shift click and middle click keep the browser behaviour (new tab, window)
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) {
          return;
        }
        event.preventDefault();
        onNavigate?.(href);
      }}
    >
      {children}
    </a>
  );
}

export function StatusPill({ status }) {
  const label = useStatusLabel();
  if (!status) return null;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-gray-700">
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: statusColor(status) }}
        aria-hidden="true"
      />
      {label(status)}
    </span>
  );
}

export function Tag({ children, tone = "gray", style }) {
  const tones = {
    gray: "bg-gray-100 text-gray-600",
    primary: "bg-primary-soft text-primary-strong",
    danger: "bg-danger-soft text-danger-strong",
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${tones[tone] || tones.gray}`}
      style={style}
    >
      {children}
    </span>
  );
}

// Client type (AAA / A / B) with the colours of the clients table
export function TypeTag({ type }) {
  if (!type) return null;
  const colors = TYPES_MAP[type];
  return (
    <Tag style={colors ? { backgroundColor: colors.bg, color: colors.text } : undefined}>
      {type}
    </Tag>
  );
}

/** Due date: "Oggi", "Domani" or the day; red when past and not delivered. */
export function DueDate({ value, done = false }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  if (!value) return null;
  const days = daysFromToday(value);
  const overdue = !done && days !== null && days < 0;
  let text = format.day(value);
  if (days === 0) text = t("blocks.due.today");
  else if (days === 1) text = t("blocks.due.tomorrow");
  return (
    <span
      className={`text-[12px] whitespace-nowrap tabular-nums ${overdue ? "font-medium text-danger-strong" : "text-gray-500"}`}
    >
      {overdue ? t("blocks.due.overdueOn", { date: text }) : text}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------
// Frame, rows, chart

/**
 * Card around a block: title, count, optional action (link), footer note when the list was cut
 * by the server ("mostrati N di count").
 */
export function BlockFrame({ title, subtitle, count, shown, truncated, action, children }) {
  const { t } = useTranslation("aiAssistant");
  return (
    <div className="surface-card overflow-hidden">
      {(title || action) && (
        <div className="flex items-start gap-2 border-b border-gray-100 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-gray-900">
              <span className="truncate">{title}</span>
              {typeof count === "number" && (
                <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600 tabular-nums">
                  {count}
                </span>
              )}
            </p>
            {subtitle && <p className="mt-0.5 truncate text-[12px] text-gray-500">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
      {truncated && typeof count === "number" && (
        <p className="border-t border-gray-100 px-3 py-2 text-[12px] text-gray-500">
          {t("blocks.truncated", { shown, count })}
        </p>
      )}
    </div>
  );
}

export function EmptyRows() {
  const { t } = useTranslation("aiAssistant");
  return <p className="px-3 py-3 text-[13px] text-gray-500">{t("blocks.empty")}</p>;
}

function ShowAllButton({ expanded, total, onClick }) {
  const { t } = useTranslation("aiAssistant");
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className="w-full border-t border-gray-100 px-3 py-2 text-left text-[12px] font-medium text-primary-strong transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
    >
      {expanded ? t("blocks.showLess") : t("blocks.showAll", { count: total })}
    </button>
  );
}

/**
 * Rows of a list block. In the panel only the first COMPACT_ROWS, with "Mostra tutti".
 * renderRow(item, index) returns the content of one <li>.
 */
export function RowList({ items, variant, renderRow, getKey }) {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return <EmptyRows />;
  const compact = variant !== "page" && list.length > COMPACT_ROWS;
  const visible = compact && !expanded ? list.slice(0, COMPACT_ROWS) : list;
  return (
    <>
      <ul className="divide-y divide-gray-100">
        {visible.map((item, index) => (
          <li key={getKey?.(item, index) ?? item?.id ?? index} className="px-3 py-2.5">
            {renderRow(item, index)}
          </li>
        ))}
      </ul>
      {compact && (
        <ShowAllButton
          expanded={expanded}
          total={list.length}
          onClick={() => setExpanded((value) => !value)}
        />
      )}
    </>
  );
}

/**
 * Standard row: title (link) with meta line below, status / date on the right.
 * In the page the meta line wraps, in the panel it is cut.
 */
export function Row({ title, href, onNavigate, meta, aside, extra, variant, leading }) {
  const wrap = variant === "page" ? "" : "truncate";
  return (
    <div className="flex items-start gap-3">
      {leading}
      <div className="min-w-0 flex-1">
        <BlockLink
          href={href}
          onNavigate={onNavigate}
          className={`block text-[14px] leading-snug font-medium text-gray-900 ${wrap}`}
        >
          {title}
        </BlockLink>
        {meta && <div className={`mt-0.5 text-[12px] text-gray-500 ${wrap}`}>{meta}</div>}
        {extra}
      </div>
      {aside && <div className="flex shrink-0 flex-col items-end gap-1">{aside}</div>}
    </div>
  );
}

/**
 * Horizontal bars (HTML/CSS): one row per group with label, bar and number. The numbers are
 * text, the bars are decoration (aria-hidden).
 * rows: [{ key, label, count, color? }]
 */
export function BarChart({ rows, total, count, multi, variant, label }) {
  const { t } = useTranslation("aiAssistant");
  if (!rows.length) return <EmptyRows />;
  const max = Math.max(1, ...rows.map((row) => row.count));
  // A truncated result: the bars describe only the rows that came back, not the whole set
  const sample = typeof count === "number" && count > total;
  const labelColumn = variant === "page" ? "minmax(0,12rem)" : "minmax(0,7.5rem)";
  return (
    <div className="px-3 py-3">
      <ul className="space-y-2" aria-label={label}>
        {rows.map((row) => (
          <li
            key={row.key}
            className="grid items-center gap-3 text-[13px]"
            style={{ gridTemplateColumns: `${labelColumn} 1fr auto` }}
          >
            <span className="truncate text-gray-700" title={row.label}>
              {row.label}
            </span>
            <span className="h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.max(4, Math.round((row.count / max) * 100))}%`,
                  backgroundColor: row.color || "var(--color-primary)",
                }}
              />
            </span>
            <span className="min-w-6 text-right font-medium text-gray-900 tabular-nums">
              {row.count}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-gray-500">
        {sample
          ? t("blocks.chartSample", { shown: total, count })
          : t("blocks.total", { count: total })}
        {multi && ` ${t("blocks.multiGroup")}`}
      </p>
    </div>
  );
}

/**
 * Bar chart of a by_* view, with the list of the items behind a "Mostra elenco" toggle.
 * groups: output of groupCount; labelOf(key) / colorOf(key) for each group.
 */
export function GroupedView({
  groups,
  labelOf,
  colorOf,
  total,
  count,
  multi,
  variant,
  label,
  list,
}) {
  const { t } = useTranslation("aiAssistant");
  const [showList, setShowList] = useState(false);
  const rows = groups.map(({ key, count }) => ({
    key,
    count,
    label: key === NONE_KEY ? labelOf(NONE_KEY) : labelOf(key),
    color: colorOf?.(key),
  }));
  return (
    <>
      <BarChart
        rows={rows}
        total={total}
        count={count}
        multi={multi}
        variant={variant}
        label={label}
      />
      {list && (
        <>
          <button
            type="button"
            onClick={() => setShowList((value) => !value)}
            aria-expanded={showList}
            className="w-full border-t border-gray-100 px-3 py-2 text-left text-[12px] font-medium text-primary-strong transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
          >
            {showList ? t("blocks.hideList") : t("blocks.showList")}
          </button>
          {showList && <div className="border-t border-gray-100">{list}</div>}
        </>
      )}
    </>
  );
}

/** Number tile of the card / summary blocks. */
export function StatTile({ label, value, tone }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2">
      <p className="truncate text-[11px] text-gray-500">{label}</p>
      <p
        className={`truncate text-[16px] font-semibold tabular-nums ${tone === "danger" ? "text-danger-strong" : "text-gray-900"}`}
      >
        {value}
      </p>
    </div>
  );
}
